import { useEffect, useRef, useState } from "react";

import { Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { Loading } from "@/components/Loading";
import ProcedureInformationDrawer from "@/components/ProcedureInformationDrawer";
import ScrollableTable from "@/components/ScrollableTable";

import { EditFeesDrawer, ProcedureRow, ProcedureTableHeader } from "../components";
import { TABLE_MIN_WIDTH } from "../constants/fee-table";
import { useProcedureFees } from "../hooks/useProcedureFees";
import { ProcedureCode } from "../types/fee-table";
import { EditFeesFormType } from "../zod-schemas/edit-fees";

interface NonCustomFeeTableProps {
  valueSetUrl: string;
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
  readOnly?: boolean;
}

export default function NonCustomFeeTable({
  valueSetUrl,
  refreshKey = 0,
  onLoadingChange,
  readOnly = false,
}: NonCustomFeeTableProps) {
  const [isEditDrawerOpen, { open: openEditDrawer, close: closeEditDrawer }] = useDisclosure(false);
  const [isInfoDrawerOpen, { open: openInfoDrawer, close: closeInfoDrawer }] = useDisclosure(false);
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureCode | null>(null);
  const [updatingProcedures, setUpdatingProcedures] = useState<Set<string>>(new Set());

  const { procedures, setProcedures, isLoading, upsertProcedureFee } = useProcedureFees(
    valueSetUrl,
    refreshKey,
  );

  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;

  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);

  const handleEdit = (procedure: ProcedureCode) => {
    setSelectedProcedure(procedure);
    openEditDrawer();
  };

  const handleEditClose = () => {
    closeEditDrawer();
    setSelectedProcedure(null);
  };

  const handleInfo = (procedure: ProcedureCode) => {
    setSelectedProcedure(procedure);
    openInfoDrawer();
  };

  const handleInfoClose = () => {
    closeInfoDrawer();
    setSelectedProcedure(null);
  };

  const handleSave = async (procedure: ProcedureCode, values: EditFeesFormType) => {
    await upsertProcedureFee(procedure, values, handleEditClose);
  };

  const handleStatusToggle = async (procedure: ProcedureCode, isActive: boolean) => {
    // Prevent multiple clicks while update is in progress
    if (updatingProcedures.has(procedure.code)) {
      return;
    }

    // Store the original value for potential revert
    const originalIsActive = procedure.isActive;

    // Mark procedure as updating
    setUpdatingProcedures((prev) => new Set(prev).add(procedure.code));

    // Optimistic update: update UI immediately
    setProcedures((prev) => prev.map((p) => (p.code === procedure.code ? { ...p, isActive } : p)));

    try {
      await upsertProcedureFee(procedure, {
        insuranceFee: procedure.insuranceFee ?? null,
        selfPayFee: procedure.selfPayFee ?? null,
        isActive,
      });
    } catch {
      // Revert the switch on error - restore original value
      setProcedures((prev) =>
        prev.map((p) => (p.code === procedure.code ? { ...p, isActive: originalIsActive } : p)),
      );
    } finally {
      // Remove from updating set when done
      setUpdatingProcedures((prev) => {
        const next = new Set(prev);
        next.delete(procedure.code);
        return next;
      });
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <ScrollableTable
        miw={TABLE_MIN_WIDTH}
        scrollProps={{ mt: 0, mb: 0 }}
        scrollStyle={{ borderRadius: 0, border: "none" }}
      >
        <ProcedureTableHeader />
        <Table.Tbody>
          {procedures.map((procedure) => (
            <ProcedureRow
              readOnly={readOnly}
              onEdit={handleEdit}
              onInfo={handleInfo}
              key={procedure.code}
              procedure={procedure}
              onStatusToggle={handleStatusToggle}
              isUpdating={updatingProcedures.has(procedure.code)}
            />
          ))}
        </Table.Tbody>
      </ScrollableTable>
      <EditFeesDrawer
        onSave={handleSave}
        opened={isEditDrawerOpen}
        onClose={handleEditClose}
        procedure={selectedProcedure}
      />
      <ProcedureInformationDrawer
        opened={isInfoDrawerOpen}
        onClose={handleInfoClose}
        title={selectedProcedure?.name}
        description={selectedProcedure?.information}
      />
    </>
  );
}
